import { useRef, useState } from "react";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/flowly/infra/Button/Button";
import { ACCEPTED_IMAGE_TYPES_STRING } from "@/config/media";

interface ThumbnailImage {
  id: string;
  url: string;
}

interface ReferenceThumbnailStripProps {
  images: ThumbnailImage[];
  onRemove: (id: string) => void;
  onAdd: (files: FileList) => void;
  maxImages: number;
  hint?: string;
  highlightColor?: "amber" | "primary";
}

export function ReferenceThumbnailStrip({
  images,
  onRemove,
  onAdd,
  maxImages,
  hint,
  highlightColor = "primary",
}: ReferenceThumbnailStripProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const canAddMore = images.length < maxImages;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    onAdd(files);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!canAddMore) return;
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onAdd(files);
    }
  };

  const dragHighlight = highlightColor === "amber"
    ? "border-amber-500 bg-amber-500/10"
    : "border-primary bg-primary/10";

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES_STRING}
        multiple
        onChange={handleInputChange}
        className="hidden"
      />

      <div className="flex flex-wrap gap-3 items-start">
        {images.map((img) => (
          <div
            key={img.id}
            className="relative w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border border-brand/20 hover:border-brand/40 transition-colors group"
          >
            <img
              src={img.url}
              alt="Reference"
              className="w-full h-full object-cover"
            />
            <Button
              variant="ghost"
              size="xs"
              rounded="xl"
              onClick={() => onRemove(img.id)}
              className="absolute inset-0 w-full h-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-none p-0"
            >
              <X className="w-4 h-4 text-white" />
            </Button>
          </div>
        ))}

        {canAddMore && (
          <Button
            variant="ghost"
            size="xs"
            rounded="xl"
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            className={cn(
              "w-16 h-16 md:w-20 md:h-20 border-2 border-dashed flex items-center justify-center transition-all",
              isDragging
                ? dragHighlight
                : "border-brand/20 bg-brand/5 hover:border-brand/40 hover:bg-brand/10",
            )}
          >
            <Plus className="w-5 h-5 text-brand" />
          </Button>
        )}
      </div>

      {hint && (
        <p className="text-xs text-muted-foreground mt-2">{hint}</p>
      )}
    </div>
  );
}
